import openPay from '../../../engines/openPay';
import { isMaskedCardNumber, maskCardNumber } from '../../../utils/cardNumberMask';

// @ts-check

/**
 * @typedef {import('./CardStrategy').CardStrategy} CardStrategy
 */

const MIN_CARD_DIGITS = 12;

/**
 * Picks the masked card number to send: OpenPay's own mask when it returns one, otherwise a mask
 * built from the BIN and last four of the entered number. Never returns the raw number.
 * @param {string | undefined} openPayCardNumber - `card.card_number` from the OpenPay token.
 * @param {string} enteredCardNumber - The card number the buyer entered.
 * @returns {string | undefined} The masked card number, or undefined when none can be built.
 */
const resolveMaskedCardNumber = (openPayCardNumber, enteredCardNumber) => {
  if (isMaskedCardNumber(openPayCardNumber)) return openPayCardNumber;

  const digits = String(enteredCardNumber || '').replace(/\D/g, '');
  return digits.length >= MIN_CARD_DIGITS ? maskCardNumber(digits) : undefined;
};

/**
 * OpenPay card strategy.
 * @function OpenPayStrategy
 * @returns {CardStrategy}
 */
const OpenPayStrategy = () => {
  /**
   * Create a payload for OpenPay.
   * @async
   * @function createPayload
   * @param {Object} cardInfo - The card details.
   * @param {string} cardInfo.cardNumber - The card number.
   * @param {string} cardInfo.holderName - The card holder's name.
   * @param {string} cardInfo.expirationYear - The card's expiration year.
   * @param {string} cardInfo.expirationMonth - The card's expiration month.
   * @param {string} cardInfo.cvv2 - The card's CVV.
   * @param {string} cardInfo.cardBrand - The card's brand.
   * @returns {Promise<Object>} The payload for OpenPay.
   * @throws {Error} If token creation fails.
   */
  const createPayload = async (cardInfo) => {
    try {
      const openPayToken = await openPay.generateToken({
        card_number: cardInfo.cardNumber,
        holder_name: cardInfo.holderName,
        expiration_year: cardInfo.expirationYear.toString().slice(-2),
        expiration_month: cardInfo.expirationMonth,
        cvv2: cardInfo.cvv2,
      });

      let payload = {
        credit_card_tokens: {
          open_pay_token: openPayToken.id,
        },
        device_session_id: window.OpenPay.deviceData.setup(),
      };

      // MOB-1011: the backend reads the BIN and last four from `card_number` to send them to
      // Riskified before the charge. Only a masked number goes here, never the raw PAN.
      const maskedCardNumber = resolveMaskedCardNumber(
        openPayToken.card?.card_number,
        cardInfo.cardNumber,
      );

      payload = {
        ...payload,
        card_holder_name: cardInfo.holderName,
        card_brand: cardInfo.cardBrand,
        ...(maskedCardNumber && { card_number: maskedCardNumber }),
      };

      return payload;
    } catch (error) {
      throw new Error('Failed to create OpenPay token. Please try again.', { cause: error });
    }
  };

  return { createPayload };
};

const openPayStrategy = OpenPayStrategy();

export default openPayStrategy;
